import {Fragment} from 'react'
import {useLocation} from 'react-router'
import {checkIsActive} from 'utility/checkvalidUrl'
import {useIntl} from 'react-intl'
import {usePermissions} from '@services/helper/permission.helper'
import {SidebarMenuItem} from './SidebarMenuItem'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'
import {IMenuItemConfig, menus, topItems} from './menu-config'

/**
 * The whole stock navigation: the top items (dashboard, till), then the groups of menu-config.
 *
 * Gating is stock's: an item renders only when the caller holds its `route_code` as a feature code
 * (canSee), and a group is a CONTAINER that renders when at least one of its children does. Each
 * child is gated again by routeCode in PrivateRoutes, so the menu can never reveal a screen the user
 * was not granted.
 */
export const SidebarMenuMain = () => {
  const intl = useIntl()
  const {canSee} = usePermissions()
  const t = (id: string) => intl.formatMessage({id})
  const {pathname} = useLocation()

  // Only the most specific match is current: an "add" shortcut lives beside its list page, and the
  // list's url is a prefix of the add form's, so both would otherwise light up on the form.
  const activeTo = [...topItems, ...menus.flatMap((g) => g.children)]
    .filter((it) => checkIsActive(pathname, it.to))
    .sort((a, b) => b.to.length - a.to.length)[0]?.to

  const item = (it: IMenuItemConfig) => (
    <SidebarMenuItem
      to={it.to}
      title={t(it.title)}
      fontIcon={it.icon}
      custIcon={it.cust_icon}
      isActive={it.to === activeTo}
    />
  )

  return (
    <>
      {topItems
        .filter((it) => canSee(it.route_code))
        .map((it) => (
          <Fragment key={it.to}>{item(it)}</Fragment>
        ))}

      {menus.map((group) => {
        const kids = group.children.filter((c) => canSee(c.route_code))
        if (!kids.length) return null
        return (
          <SidebarMenuItemWithSub
            key={group.route_code || group.title}
            id={group.route_code || group.title}
            title={t(group.title)}
            fontIcon={group.icon}
            childUrls={kids.map((c) => c.to)}
            defaultOpen={group.open_by_default}
          >
            {kids.map((c, i) => (
              // Keyed on the path: two entries may share one route_code.
              <Fragment key={c.to}>
                {c.has_separator && i > 0 && <div className='app-sidebar-sep'></div>}
                {item(c)}
              </Fragment>
            ))}
          </SidebarMenuItemWithSub>
        )
      })}
    </>
  )
}
